import { DispatchContext } from 'contexts/dispatch.context';
import type { AppEnvironment } from 'contexts/environment/environment.context';
import { EnvironmentContext } from 'contexts/environment/environment.context';
import type { ReportErrorCallback } from 'contexts/errorReportingContext';
import { ErrorReportingContext } from 'contexts/errorReportingContext';
import { SgBsThemeContext } from 'contexts/sgBsTheme.context';
import { ToggleFeaturesContext } from 'contexts/toggleFeatures.context';
import { UnderlyingCodeTypeContext } from 'contexts/underlyingCodeTypeContext';
import type { UserType } from 'contexts/user.context';
import { UserTypeContext } from 'contexts/user.context';
import type { Features } from 'models/features';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { FC, ReactNode } from 'react';
import type { Dispatch } from 'redux';
import { LoadingSkeletonTheme } from 'routes/LoadingSkeletonTheme';
import type { SgBsTheme } from 'state/userPreferences/userPreferences.model';

import { AppNotReady } from './AppNotReady';

interface AppWrapperProps {
  children: ReactNode;
  isReady: boolean;

  userType: UserType;

  theme: SgBsTheme;
  defaultUnderlyingCodeType: UnderlyingCodeType;

  environment: AppEnvironment;

  features: Features;
  dispatch: Dispatch<any>;

  reportError: ReportErrorCallback;
}

export const AppWrapper: FC<AppWrapperProps> = ({
  isReady,
  theme,
  defaultUnderlyingCodeType,
  userType,
  environment,
  features,
  dispatch,
  children,
  reportError,
}) => {
  if (!isReady) {
    return <AppNotReady />;
  }

  return (
    <ErrorReportingContext.Provider value={reportError}>
      <DispatchContext.Provider value={dispatch}>
        <UserTypeContext.Provider value={userType}>
          <SgBsThemeContext.Provider value={theme}>
            <UnderlyingCodeTypeContext.Provider value={defaultUnderlyingCodeType}>
              <EnvironmentContext.Provider value={environment}>
                <ToggleFeaturesContext.Provider value={features}>
                  <LoadingSkeletonTheme theme={theme}>{children}</LoadingSkeletonTheme>
                </ToggleFeaturesContext.Provider>
              </EnvironmentContext.Provider>
            </UnderlyingCodeTypeContext.Provider>
          </SgBsThemeContext.Provider>
        </UserTypeContext.Provider>
      </DispatchContext.Provider>
    </ErrorReportingContext.Provider>
  );
};
AppWrapper.displayName = 'AppComponent';
